import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

export const generateTicketsListPDF = async (tickets, t, dateFormat, formatDateTimeBySetting, translateStatus, translatePriority, getStatusColor, getPriorityColor, currentLanguage, filterInfo = {}) => {
  try {
    console.log('Starting tickets list PDF generation...', 'Language:', currentLanguage);
    
    // Detect if Chinese (check for 'zh' or 'zh-TW')
    const isChinese = currentLanguage && (currentLanguage.startsWith('zh') || currentLanguage === 'zh-TW');
    
    // Build filter display text
    const filterTexts = [];
    if (filterInfo.searchTerm) {
      filterTexts.push(`${isChinese ? '搜尋' : 'Search'}: ${filterInfo.searchTerm}`);
    }
    if (filterInfo.projectFilter && filterInfo.projectFilter !== 'all' && filterInfo.projectName) {
      filterTexts.push(`${isChinese ? '專案' : 'Project'}: ${filterInfo.projectName}`);
    }
    if (filterInfo.statusFilter && filterInfo.statusFilter !== 'all') {
      filterTexts.push(`${isChinese ? '狀態' : 'Status'}: ${translateStatus(filterInfo.statusFilter)}`);
    }
    if (filterInfo.priorityFilter && filterInfo.priorityFilter !== 'all') {
      filterTexts.push(`${isChinese ? '優先級' : 'Priority'}: ${translatePriority(filterInfo.priorityFilter)}`);
    }
    if (filterInfo.categoryFilter && filterInfo.categoryFilter !== 'all') {
      filterTexts.push(`${isChinese ? '類別' : 'Category'}: ${filterInfo.categoryFilter}`);
    }
    if (filterInfo.timeFilter && filterInfo.timeFilter !== 'all') {
      if (filterInfo.timeFilter === 'custom' && filterInfo.customDateFrom && filterInfo.customDateTo) {
        filterTexts.push(`${isChinese ? '日期' : 'Date'}: ${filterInfo.customDateFrom} ~ ${filterInfo.customDateTo}`);
      } else {
        const timeFilterMap = {
          'today': isChinese ? '今天' : 'Today',
          'week': isChinese ? '最近7天' : 'Last 7 days',
          'month': isChinese ? '最近30天' : 'Last 30 days'
        };
        filterTexts.push(`${isChinese ? '日期' : 'Date'}: ${timeFilterMap[filterInfo.timeFilter] || filterInfo.timeFilter}`);
      }
    }
    const filterDisplayText = filterTexts.length > 0 ? filterTexts.join(' | ') : (isChinese ? '無篩選條件' : 'No filters applied');
    
    // Create a hidden container for rendering
    const container = document.createElement('div');
    container.style.cssText = `
      position: absolute;
      left: -9999px;
      top: 0;
      width: 1122px;
      background: white;
      padding: 40px;
      font-family: 'Noto Sans TC', 'Microsoft JhengHei', sans-serif;
    `;
    
    document.body.appendChild(container);
    
    // Get current date
    const currentDate = new Date().toLocaleDateString(isChinese ? 'zh-TW' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    // Calculate ticket summary by status
    const statusCounts = {};
    tickets.forEach(ticket => {
      const status = ticket.status || 'unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });
    
    // Split tickets into chunks of 15 rows per page
    const rowsPerPage = 15;
    const totalPages = Math.ceil(tickets.length / rowsPerPage);
    
    // Create PDF in landscape A4
    const pdf = new jsPDF('l', 'mm', 'a4'); // 'l' = landscape
    
    // Render each page separately to avoid header splitting
    for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
      console.log(`Rendering page ${pageIndex + 1}/${totalPages}...`);
      
      const startIndex = pageIndex * rowsPerPage;
      const endIndex = Math.min(startIndex + rowsPerPage, tickets.length);
      const pageTickets = tickets.slice(startIndex, endIndex);
      
      // Build HTML for this specific page only
      let pageHtml = `
        <div style="font-family: 'Noto Sans TC', 'Microsoft JhengHei', sans-serif; font-size: 12px; line-height: 1.4; color: #000000;">
          <!-- Header -->
          <div style="text-align: center; margin-bottom: 20px;">
            <h1 style="font-size: 24px; font-weight: bold; margin: 0 0 10px 0; color: #000000; line-height: 1.2;">
              ${isChinese ? '工程缺失改善紀錄表' : 'Project Tickets List'}
            </h1>
            <div style="font-size: 14px; color: #666666;">${currentDate}</div>
          </div>
      `;
      
      // Add filter information on first page only
      if (pageIndex === 0) {
        pageHtml += `
          <!-- Filter Information -->
          <div style="margin-bottom: 5px;">
            <div style="font-size: 11px; color: #555;">
              <strong>${isChinese ? '篩選條件' : 'Filter Conditions'}:</strong> ${filterDisplayText}
            </div>
          </div>
        `;
      }
      
      pageHtml += `
          <!-- Table -->
          <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
            <thead>
              <tr style="background: #f5f5f5; border-bottom: 2px solid #333;">
                <th style="text-align: left; padding: 10px 8px; font-weight: bold; border: 1px solid #ddd;">${t('tickets.ticketCode')}</th>
                <th style="text-align: left; padding: 10px 8px; font-weight: bold; border: 1px solid #ddd;">${t('tickets.ticketTitle')}</th>
                <th style="text-align: left; padding: 10px 8px; font-weight: bold; border: 1px solid #ddd;">${t('navigation.projects')}</th>
                <th style="text-align: left; padding: 10px 8px; font-weight: bold; border: 1px solid #ddd;">${t('floors.floorPlan')}</th>
                <th style="text-align: left; padding: 10px 8px; font-weight: bold; border: 1px solid #ddd;">${t('tickets.status')}</th>
                <th style="text-align: left; padding: 10px 8px; font-weight: bold; border: 1px solid #ddd;">${t('tickets.priority')}</th>
                <th style="text-align: left; padding: 10px 8px; font-weight: bold; border: 1px solid #ddd;">${t('tickets.category')}</th>
                <th style="text-align: left; padding: 10px 8px; font-weight: bold; border: 1px solid #ddd;">${t('tickets.createdAt')}</th>
              </tr>
            </thead>
            <tbody>
      `;
      
      // Add ticket rows for this page
      for (const ticket of pageTickets) {
        const statusColor = getStatusColor(ticket.status);
        const priorityColor = getPriorityColor(ticket.priority);
        
        pageHtml += `
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 8px; border: 1px solid #ddd; font-family: monospace; font-size: 10px; vertical-align: middle;">${ticket.ticket_code || 'N/A'}</td>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: 500; vertical-align: middle;">${ticket.title || ''}</td>
            <td style="padding: 8px; border: 1px solid #ddd; vertical-align: middle;">${ticket.project_name || ''}</td>
            <td style="padding: 8px; border: 1px solid #ddd; vertical-align: middle;">${ticket.floor_name || ''}</td>
            <td style="padding: 8px; border: 1px solid #ddd; text-align: center; vertical-align: middle;">
              <span style="background: ${getStatusBgColor(statusColor)}; color: white; padding: 6px 8px 5px 8px; border-radius: 4px; font-size: 10px; display: inline-block; line-height: 1;">
                ${translateStatus(ticket.status)}
              </span>
            </td>
            <td style="padding: 8px; border: 1px solid #ddd; text-align: center; vertical-align: middle;">
              <span style="background: ${getPriorityBgColor(priorityColor)}; color: white; padding: 6px 8px 5px 8px; border-radius: 4px; font-size: 10px; display: inline-block; line-height: 1;">
                ${translatePriority(ticket.priority)}
              </span>
            </td>
            <td style="padding: 8px; border: 1px solid #ddd; text-transform: capitalize; vertical-align: middle;">${ticket.category || ''}</td>
            <td style="padding: 8px; border: 1px solid #ddd; font-size: 10px; vertical-align: middle;">${formatDateTimeBySetting(ticket.created_at, dateFormat)}</td>
          </tr>
        `;
      }
      
      pageHtml += `
            </tbody>
          </table>
      `;
      
      // Add summary section on the last page only
      if (pageIndex === totalPages - 1) {
        pageHtml += `
          <!-- Ticket Summary -->
          <div style="margin-top: 15px;">
            <div style="font-size: 14px; font-weight: bold; margin-bottom: 15px; color: #000000;">
              ${isChinese ? '缺失統計' : 'Ticket Summary'}
            </div>
            <div style="display: flex; gap: 20px; flex-wrap: wrap;">
        `;
        
        // Add each status count with color
        for (const [status, count] of Object.entries(statusCounts)) {
          const statusColor = getStatusColor(status);
          const bgColor = getStatusBgColor(statusColor);
          const translatedStatus = translateStatus(status);
          
          pageHtml += `
            <div style="text-align: center; min-width: 100px;">
              <div style="font-size: 32px; font-weight: bold; color: ${bgColor}; line-height: 1; margin-bottom: 8px;">${count}</div>
              <div style="font-size: 12px; color: #333; font-weight: 500;">${translatedStatus}</div>
            </div>
          `;
        }
        
        pageHtml += `
            </div>
          </div>
        `;
      }
      
      pageHtml += `
        </div>
      `;
      
      // Render this page
      container.innerHTML = pageHtml;
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Capture with html2canvas
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      // Add page to PDF
      if (pageIndex > 0) {
        pdf.addPage();
      }
      
      const imgWidth = 297; // A4 landscape width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      
      pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
    }
    
    // Remove container
    document.body.removeChild(container);
    
    // Download PDF
    const fileName = `Tickets_List_${new Date().getTime()}.pdf`;
    pdf.save(fileName);
    
    console.log('Tickets list PDF generated successfully!');
  } catch (error) {
    console.error('Error generating tickets list PDF:', error);
    throw error;
  }
};

// Helper functions for colors
function getStatusBgColor(colorClass) {
  if (colorClass.includes('bg-green')) return '#22c55e';
  if (colorClass.includes('bg-blue')) return '#3b82f6';
  if (colorClass.includes('bg-yellow')) return '#eab308';
  if (colorClass.includes('bg-red')) return '#ef4444';
  if (colorClass.includes('bg-gray')) return '#6b7280';
  return '#6b7280';
}

function getPriorityBgColor(colorClass) {
  // Priority colors: urgent=red, high=orange, medium=yellow, low=green
  if (colorClass.includes('bg-red')) return '#ef4444';
  if (colorClass.includes('bg-orange')) return '#f97316';
  if (colorClass.includes('bg-yellow')) return '#eab308';
  if (colorClass.includes('bg-green')) return '#22c55e';
  if (colorClass.includes('bg-gray')) return '#6b7280';
  return '#6b7280';
}
